import type { GaborGrangerRung } from "../types";
import { DEFAULT_CURRENCY_CODE } from "./index";

// The fixed 5-point purchase-intent scale. `value` is stored unthresholded, so
// accept/decline is derived via `acceptIntentThreshold` rather than baked in.
// Persisted transcript labels, not respondent UI. Do not translate.
export const GABOR_GRANGER_INTENT_SCALE = [
  { value: 1, label: "Definitely would not buy" },
  { value: 2, label: "Probably would not buy" },
  { value: 3, label: "Might or might not buy" },
  { value: 4, label: "Probably would buy" },
  { value: 5, label: "Definitely would buy" },
] as const;

// Locale-correct range ("$5.00 - $25.00", "5,00-25,00 $US"), so a caller can name
// the ladder's span without inventing a joiner word that would need translating.
export const formatPriceRange = (
  low: number,
  high: number,
  currencyCode: string = DEFAULT_CURRENCY_CODE,
  locale?: string
): string => {
  // Equal bounds format as "~$15.00", which reads as an estimate rather than a
  // one-rung ladder, so name the single price instead.
  if (low === high) return formatPrice(low, currencyCode, locale);
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
    }).formatRange(low, high);
  } catch {
    return `${formatPrice(low, currencyCode, locale)} - ${formatPrice(high, currencyCode, locale)}`;
  }
};

export const getIntentLabel = (intentValue: number | undefined): string =>
  GABOR_GRANGER_INTENT_SCALE.find((o) => o.value === intentValue)?.label ??
  String(intentValue ?? "");

// Falls back to a plain `${code} ${amount}` when `Intl` does not recognize the
// currency, so an unusual authored code degrades rather than throws mid-interview.
// Pass the respondent's language where one is known: this runs on the server for
// interview v2, where the host locale would otherwise format every respondent's
// price in en-US.
export const formatPrice = (
  price: number,
  currencyCode: string = DEFAULT_CURRENCY_CODE,
  locale?: string
): string => {
  try {
    return new Intl.NumberFormat(locale, {
      style: "currency",
      currency: currencyCode,
    }).format(price);
  } catch {
    return `${currencyCode} ${price}`;
  }
};

// Just the symbol, for prefixing an input the author types a bare number into.
// Same fallback as formatPrice: an unrecognized code degrades to the code itself.
export const getCurrencySymbol = (
  currencyCode: string = DEFAULT_CURRENCY_CODE
): string => {
  try {
    return (
      new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: currencyCode,
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value ?? currencyCode
    );
  } catch {
    return currencyCode;
  }
};

// Transcript line for one answered rung, e.g. "$16.00: Probably would buy". The
// structured rung on the QAState stays the source of truth for the curve.
// English by design, like the labels above: this string is persisted.
export const formatGaborGrangerAnswer = ({
  rung,
  currencyCode,
}: {
  rung: GaborGrangerRung;
  currencyCode?: string;
}): string => {
  const priceLabel = formatPrice(rung.price, currencyCode);
  if (rung.intentValue !== undefined) {
    return `${priceLabel}: ${getIntentLabel(rung.intentValue)}`;
  }
  if (rung.accepted !== undefined) {
    return `${priceLabel}: ${rung.accepted ? "Would buy" : "Would not buy"}`;
  }
  return priceLabel;
};
